import React from 'react'
import { FaDownload } from 'react-icons/fa'
import { Heading, Text, Box, Stack, Loading, Button, Alert, Stat, Icon, EmptyBox, Badge } from '@/components/lib'
import utils, { formatKycaidAddresses } from '@/utils/utils'

export default function Consent ({ trialId, participantId, participant }) {
  const consent = participant?.trialConsentObject
  const consentVerification = participant?.trialConsentVerificationObject
  const iv = consent?.identityVerificationObject
  const applicant = iv ? (iv.kycaidData || {}).applicant || {} : {}

  if (!participant) {
    return (
      <Loading />
    )
  }

  return (
    <Stack gap={5}>

      {consent
        ? (
          <>
            <Alert status='success'>The participant has completed consent verification for this trial.</Alert>

            <Stack bg='bg.surface' rounded='md' p={3}>
              <Box display='flex' justifyContent='space-between'>
                <Stack>
                  <Heading size='md'>Consent details</Heading>
                  <Text>Before joining the trial, the participant consented to take part.</Text>
                </Stack>
                {consent.fileUrl &&
                  <Button size='sm' colorPalette='primary' as='a' target='_blank' href={consent.fileUrl}><Icon as={FaDownload} /> Download consent</Button>}
              </Box>

              <Stat
                label='Consent recorded at'
                value={utils.formatDateTime(consent.createdAt)}
              />
              {consent.fullName &&
                <Stat
                  label='Consent name'
                  value={consent.fullName}
                />}
              {consent.phone &&
                <Stat
                  label='Consent phone number'
                  value={consent.phone}
                />}
              {consent.whatsapp &&
                <Stat
                  label='Consent WhatsApp number'
                  value={consent.whatsapp}
                />}
              {consent.email &&
                <Stat
                  label='Consent email address'
                  value={consent.email}
                />}
            </Stack>

            {iv &&
              <Stack bg='bg.surface' rounded='md' p={3}>
                <Heading size='md'>Identity verification</Heading>
                <Text mb={2}>The participant verified their identity during eConsent using a government-issued ID and selfie.</Text>
                <Stat
                  label='Status'
                  value={<Badge colorPalette={iv.status === 'verified' ? 'green' : iv.status === 'failed' ? 'red' : 'gray'} size='sm'>{iv.status}</Badge>}
                />
                <Stat
                  label='Name'
                  value={[applicant.first_name, applicant.last_name].filter(Boolean).join(' ').trim() || '—'}
                />
                <Stat
                  label='Address'
                  value={<Text whiteSpace='pre-line'>{formatKycaidAddresses(applicant.addresses)}</Text>}
                />
                <Stat
                  label='Gender'
                  value={(applicant.gender || '—').toUpperCase()}
                />
                <Stat
                  label='Documents used'
                  value={(applicant.documents || []).map(d => d.type).filter(Boolean).join(', ') || '—'}
                />
                <Stat
                  label='Decline reasons'
                  value={(iv.kycaidDeclineReasons || []).length ? (iv.kycaidDeclineReasons || []).join(', ') : 'None'}
                />
              </Stack>}

            {consentVerification &&
              <Stack bg='bg.accentSubtle' rounded='md' p={3}>
                <Heading size='md'>Consent verification</Heading>
                <Text>When joining the trial, the participant verified their consent via a two-factor code sent to one of their chosen contact methods.</Text>
                {consentVerification?.consumedAt &&
                  <Stat
                    label='Consent verified at'
                    value={utils.formatDateTime(consentVerification.consumedAt)}
                  />}
                {consentVerification?.phone &&
                  <Stat
                    label='Consent verified via'
                    value={`SMS to: ${consentVerification.phone}`}
                  />}
                {consentVerification?.whatsapp &&
                  <Stat
                    label='Consent verified via'
                    value={`WhatsApp to: ${consentVerification.whatsapp}`}
                  />}
                {consentVerification?.email &&
                  <Stat
                    label='Consent verified via'
                    value={`Email to: ${consentVerification.email}`}
                  />}
              </Stack>}
          </>)
        : <EmptyBox
            icon='signature'
            title='Participant has not yet consented' description={'If the trial has "Consent" enabled, the participant\'s consent information will appear here when the participant joins.'}
          />}

    </Stack>
  )
}
